import React, { useEffect, useState } from 'react';
import {
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import { Switch } from '@/src/shadComponents/ui/switch';
import { Label } from '@/src/shadComponents/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/src/shadComponents/ui/select';
import { Input } from '@/src/shadComponents/ui/input';
import { Button } from '@/src/shadComponents/ui/button';
import useRekognition from './hooks/useRekognition';
import { toast } from 'sonner';

interface RekognitionSettingsProps {
    activationId: number;
}

interface IRekognitionSettings {
    id: number;
    enabled: boolean;
    type: string;
    confidence: number;
}

export default function RekognitionSettings({
    activationId,
}: RekognitionSettingsProps) {
    const rekognition = useRekognition();
    const [enabled, setEnabled] = useState(false);
    const [type, setType] = useState('text');
    const [confidence, setConfidence] = useState(97);
    const [settingsId, setSettingsId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const loadSettings = async () => {
        try {
            const response = await rekognition.getSettings(activationId);
            if (response) {
                const settings = response.data as IRekognitionSettings;
                setEnabled(settings.enabled);
                setType(settings.type);
                setConfidence(settings.confidence);
                setSettingsId(settings.id);
            }
        } catch (error) {
            console.error('Error loading settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRun = async () => {
        try {
            await rekognition.runRekognition(activationId);
            toast.success('Photos are being analyzed.');
        } catch (error) {
            console.error('Error running rekognition:', error);
            toast.error('Failed to run analysis.');
        }
    };

    const handleSave = async () => {
        try {
            if (settingsId) {
                await rekognition.updateSettings(
                    settingsId,
                    enabled,
                    type,
                    confidence,
                );
                toast.success('Settings updated successfully');
            } else {
                const response = await rekognition.createSettings(
                    activationId,
                    enabled,
                    type,
                    confidence,
                );
                const settings = response.data as IRekognitionSettings;
                setSettingsId(settings.id);
                toast.success('Settings created successfully');
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <CardHeader>
                <CardTitle>Media Analysis Settings</CardTitle>
                <CardDescription>
                    Configure photo and video analysis settings for this
                    activation.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="flex items-center space-x-2">
                    <Switch
                        id="enabled"
                        checked={enabled}
                        onCheckedChange={setEnabled}
                    />
                    <Label htmlFor="enabled">Enable Media Analysis</Label>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="type">What to detect</Label>
                    <Select value={type} onValueChange={setType}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="number">Numbers</SelectItem>
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="face">Faces</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="confidence">Confidence Threshold (%)</Label>
                    <Input
                        id="confidence"
                        type="number"
                        min={0}
                        max={100}
                        value={confidence}
                        onChange={(e) => setConfidence(Number(e.target.value))}
                    />
                </div>
                <div className="flex flex-row justify-end">
                    <Button onClick={handleSave}>Save Settings</Button>
                    {settingsId && (
                        <Button className="ml-2" onClick={handleRun}>
                            Run Analysis
                        </Button>
                    )}
                </div>
            </CardContent>
        </div>
    );
}
